import React from "react";
import { useTranslation } from "react-i18next";
import type { TableColumn } from "@/types/component";
import ReportColumnSettings from "../components/ReportColumnSettings";
import ReportPresetBar from "../components/ReportPresetBar";
import ReportScheduleModal from "../components/ReportScheduleModal";
import { useReportTableSettings } from "./useReportTableSettings";
import { useReportPresets } from "./useReportPresets";
import styles from "./useReportConfig.module.scss";

export interface UseReportConfigOptions<T> {
  /** Backend ReportTypeEnum eşleği — tüm ayarlar bu anahtarla saklanır. */
  reportKey: string;
  /** Rapor sayfasının başlığı (zamanlama adı için varsayılan). */
  reportTitle: string;
  /** Tablo kolonları. Yapılandırma için her kolonda `key` (ve gerekiyorsa `label`) olmalı. */
  columns?: TableColumn<T>[];
}

export interface UseReportConfigResult<T> {
  /** Kullanıcı tercihine göre sıralanmış/filtrelenmiş kolonlar. */
  columns: TableColumn<T>[];
  /** Sayfa başlığına (exportSlot) konacak "Zamanla" düğmesi. Blok düzeni
   *  düğmesini ReportDetailLayout `layoutKey` ile kendisi basar. */
  toolbar: React.ReactNode;
  /** Tablonun hemen üstüne konacak "Kolonlar" düğmesi. */
  columnSettings: React.ReactNode;
  /** Filtre çubuğunun altına konacak kayıtlı filtre setleri şeridi. */
  presetBar: (
    currentFilters: Record<string, unknown>,
    onApply: (filters: Record<string, unknown>) => void,
  ) => React.ReactNode;
  /** Sayfanın sonuna konacak zamanlama modalı. */
  scheduleModal: (currentFilters: Record<string, unknown>) => React.ReactNode;
  /** Varsayılan işaretli filtre seti (sayfa açılışında bir kez uygulanır). */
  defaultFilters: Record<string, unknown> | null;
}

/**
 * Bir rapor detay sayfasına R11 yapılandırılabilirliğini tek çağrıda ekler:
 * kolon seçme/gizleme + kolon sırası, kayıtlı filtre setleri, zamanlanmış
 * e-posta raporu ve sayfa blok düzeni (gösterge düzeni).
 *
 * 24 rapor sayfasının her birine yedi ayrı parçayı elle bağlamak hem çok
 * tekrar hem de sapma kaynağıydı; ortak sarmalayıcı sayfa başına birkaç satıra
 * indiriyor ve davranışı her sayfada aynı tutuyor.
 */
export function useReportConfig<T>(options: UseReportConfigOptions<T>): UseReportConfigResult<T> {
  const { reportKey, reportTitle, columns = [] } = options;
  const { t } = useTranslation();
  const [isScheduleOpen, setIsScheduleOpen] = React.useState(false);

  const tableSettings = useReportTableSettings<T>(reportKey, columns);
  const presets = useReportPresets<Record<string, unknown>>(reportKey);

  const toolbar = (
    <div className={styles.toolbar}>
      <button
        type="button"
        className={styles.scheduleButton}
        onClick={() => setIsScheduleOpen(true)}
      >
        {t("reportSettings.scheduleButton")}
      </button>
    </div>
  );

  // Kolon ayarı tabloya ait olduğu için tablonun hemen üstünde durur.
  const columnSettings =
    columns.length > 0 ? (
      <div className={styles.tableToolbar}>
        <ReportColumnSettings
          descriptors={tableSettings.descriptors}
          isSaving={tableSettings.isSaving}
          isCustomized={tableSettings.isCustomized}
          onToggle={tableSettings.toggleColumn}
          onMove={tableSettings.moveColumn}
          onReset={tableSettings.reset}
        />
      </div>
    ) : null;

  const presetBar = (
    currentFilters: Record<string, unknown>,
    onApply: (filters: Record<string, unknown>) => void,
  ) => (
    <ReportPresetBar
      presets={presets.presets}
      currentFilters={currentFilters}
      onApply={onApply}
      onSave={(name, filters, isDefault) => presets.save(name, filters, isDefault)}
      onDelete={presets.remove}
    />
  );

  const scheduleModal = (currentFilters: Record<string, unknown>) => (
    <ReportScheduleModal
      isOpen={isScheduleOpen}
      onClose={() => setIsScheduleOpen(false)}
      reportKey={reportKey}
      reportTitle={reportTitle}
      currentFilters={currentFilters}
    />
  );

  return {
    columns: tableSettings.columns,
    toolbar,
    columnSettings,
    presetBar,
    scheduleModal,
    defaultFilters: presets.defaultFilters,
  };
}
